import React, { useContext } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';

import classNames from 'classnames';
import FormFooter from '../../../components/form/FormikFormFooter';
import StripeElements from '../../../components/stripe/StripeElements';
import Step from '../../../components/wizard/Step';
import { DEFAULT_CURRENCY } from '../../../constants/price';
import routes from '../../../routes/constants';
import text, { formatCurrency } from '../../../text';
import ConfirmationForm from './ConfirmationForm';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useSubscribeToMembershipMutation } from '@/redux/rtk';
import { useRedirect } from '@/hooks/useRedirect';
import { useStripe } from '@stripe/react-stripe-js';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { refreshCustomer } from '../../../redux/actions';
import { confirmStripePayment } from '@/utilities/stripe';
import { useDeals } from '@/mp/hooks/useDeal';
import { MEMBERSHIP } from '@/constants/membership';
import { PaymentContext } from '../PaymentContext';

const validationSchema = yup.object({
  terms: yup.bool().oneOf([true]),
});

const getRecurringFrequency = (membershipPriceId: string) => {
  return membershipPriceId === MEMBERSHIP.MONTHLY.PRICE_ID
    ? 'monthly'
    : 'yearly';
};

const ConfirmationStep = () => {
  const redirect = useRedirect();
  const stripe = useStripe();
  const dispatch = useAppDispatch();

  const membershipPriceId = useAppSelector(
    (state) => state.order.membershipPriceId
  );
  const paymentMethodId = useAppSelector(
    (state) => state.order.paymentMethodId
  );
  const membershipPrices = useAppSelector(
    (state) => state.profileReducer.membershipPrices
  );

  const selectedMembership = membershipPrices.value.find(
    (m: any) => m.id === membershipPriceId
  );
  const totalPriceFormatted = formatCurrency(
    selectedMembership?.grand_total,
    DEFAULT_CURRENCY.name,
    DEFAULT_CURRENCY.scale_factor,
    {},
    true
  );

  const [subscribeToMembership] = useSubscribeToMembershipMutation();
  const { createDealForMembershipPaid } = useDeals();
  const paymentCtx = useContext(PaymentContext);

  const onSubmit = async () => {
    if (!membershipPriceId) {
      throw new Error('Membership price ID is required');
    }
    if (!paymentMethodId) {
      throw new Error('Payment method ID is required');
    }

    if (paymentCtx && 'actions' in paymentCtx) {
      // @ts-expect-error - PaymentContext is not typed
      paymentCtx.actions.setPaymentStatus('processing');
    }

    try {
      const response = await subscribeToMembership({
        paymentMethodId,
        priceId: membershipPriceId,
      }).unwrap();

      if (!response?.paymentIntentClientSecret) {
        throw new Error('Payment intent client secret is required');
      }

      await confirmStripePayment({
        stripe,
        paymentMethodId,
        paymentIntentClientSecret: response.paymentIntentClientSecret,
      });
      createDealForMembershipPaid({
        priceId: membershipPriceId,
        recurringFrequency: getRecurringFrequency(membershipPriceId),
      });
      dispatch(refreshCustomer());

      if (paymentCtx && 'actions' in paymentCtx) {
        // @ts-expect-error - PaymentContext is not typed
        paymentCtx.actions.setPaymentStatus(null);
      }
      redirect.push(routes.user.membershipChangeSuccess);
    } catch (err) {
      if (paymentCtx && 'actions' in paymentCtx) {
        // @ts-expect-error - PaymentContext is not typed
        paymentCtx.actions.setPaymentStatus('error');
      }
      throw err;
    }
  };

  return (
    <Formik
      initialValues={{
        terms: false,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnMount
    >
      {({ isValid }) => (
        <>
          {/* @ts-expect-error - Step component is not typed */}
          <Step>
            <ConfirmationForm />
          </Step>
          <FormFooter
            label={
              <span
                style={{ fontSize: 16 }}
                className={classNames({
                  'green-1': !isValid,
                })}
              >
                {totalPriceFormatted} - {text('payNow')}
              </span>
            }
            disabled={!isValid}
          />
        </>
      )}
    </Formik>
  );
};

const ConfirmationStepWithStripe = () => {
  return (
    <StripeElements>
      <ConfirmationStep />
    </StripeElements>
  );
};

export default ConfirmationStepWithStripe;
